import { useState, useCallback } from "react";
import clsx from "clsx";
import {
    type DailyArea2And3Option,
    type DailyShiftCellDisplaySettings,
    DAILY_AREA2_AND_3_OPTIONS,
    DAILY_SAMPLE_EMPLOYEES,
    getDailyDisplayText,
} from "../../../shared/dailyShiftSettings";

export interface DailyShiftCellSettingsProps {
    /** 現在の設定値 */
    value: DailyShiftCellDisplaySettings;
    /** 設定変更時のコールバック */
    onChange: (settings: DailyShiftCellDisplaySettings) => void;
    /** 読み取り専用 */
    readOnly?: boolean;
}

/** サンプル時間帯データ（プレビュー用、5時間分） */
const SAMPLE_HOURS = [9, 10, 11, 12, 13];

/** サンプルシフト時間 */
const SAMPLE_SHIFTS: (
    | { startHour: number; startMin: number; endHour: number; endMin: number }
    | null
)[] = [
    { startHour: 9, startMin: 0, endHour: 14, endMin: 0 },
    { startHour: 10, startMin: 0, endHour: 13, endMin: 30 },
    null,
];

/**
 * ワースケ（日別シフト）従業員セル表示設定コンポーネント
 */
export const DailyShiftCellSettings = ({
    value,
    onChange,
    readOnly = false,
}: DailyShiftCellSettingsProps) => {
    const [activeArea, setActiveArea] = useState<"area2" | "area3" | null>(null);

    const handleArea2Change = useCallback(
        (option: DailyArea2And3Option) => {
            onChange({ ...value, area2: option });
        },
        [value, onChange]
    );

    const handleArea3Change = useCallback(
        (option: DailyArea2And3Option) => {
            onChange({ ...value, area3: option });
        },
        [value, onChange]
    );

    const handleAreaClick = (area: "area2" | "area3") => {
        if (readOnly) return;
        setActiveArea(activeArea === area ? null : area);
    };

    // プレビュー: 日別シフトグリッドの従業員セル部分を模したもの
    const renderGridPreview = () => {
        const hourWidth = 60;
        const labelWidth = 280;
        const gridWidth = SAMPLE_HOURS.length * hourWidth;

        return (
            <div className="border border-gray-300 rounded overflow-hidden bg-white">
                <table className="border-separate border-spacing-0">
                    {/* 時間ヘッダー */}
                    <thead>
                        <tr className="bg-gray-100">
                            <th
                                className="border-r border-b border-gray-300 bg-gray-100"
                                style={{ width: labelWidth, minWidth: labelWidth, height: 30 }}
                            />
                            <th
                                className="border-b border-gray-300 relative"
                                style={{ width: gridWidth, minWidth: gridWidth, height: 30, padding: 0 }}
                            >
                                <div className="absolute inset-0 flex">
                                    {SAMPLE_HOURS.map((hour, idx) => (
                                        <div
                                            key={hour}
                                            className={clsx(
                                                "flex items-center justify-center",
                                                idx < SAMPLE_HOURS.length - 1 && "border-r border-gray-400"
                                            )}
                                            style={{ width: hourWidth }}
                                        >
                                            <span className="text-xs text-gray-500 font-medium">
                                                {hour}時
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {DAILY_SAMPLE_EMPLOYEES.map((employee, empIdx) => {
                            const isFirstRow = empIdx === 0;
                            const area2Text = getDailyDisplayText(value.area2, employee);
                            const area3Text = getDailyDisplayText(value.area3, employee);
                            const shift = SAMPLE_SHIFTS[empIdx] ?? null;

                            // シフトバーの位置計算
                            const shiftBarStyle = shift
                                ? {
                                      left:
                                          (shift.startHour - SAMPLE_HOURS[0]) * hourWidth +
                                          (shift.startMin / 60) * hourWidth,
                                      width:
                                          (shift.endHour - shift.startHour) * hourWidth +
                                          ((shift.endMin - shift.startMin) / 60) * hourWidth,
                                  }
                                : null;

                            return (
                                <tr
                                    key={empIdx}
                                    className="border-b border-gray-200 last:border-b-0"
                                >
                                    {/* 従業員ラベルセル */}
                                    <td
                                        className="border-r border-gray-200 bg-white"
                                        style={{ width: labelWidth, minWidth: labelWidth, height: 58 }}
                                    >
                                        <div className="px-3 h-full flex flex-col justify-center">
                                            {/* エリア1 + > ボタン */}
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {employee.employeeName}
                                                </span>
                                                <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                                            </div>
                                            {/* エリア2, 3（左右揃え） */}
                                            <div className="flex items-center justify-between mt-0.5 whitespace-nowrap">
                                                {isFirstRow ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAreaClick("area2")}
                                                            disabled={readOnly}
                                                            className={clsx(
                                                                "rounded px-1 -mx-1 transition-colors",
                                                                activeArea === "area2"
                                                                    ? "bg-brand-100 ring-2 ring-brand-500"
                                                                    : "hover:bg-gray-100",
                                                                readOnly && "cursor-default"
                                                            )}
                                                        >
                                                            <span className="text-xs text-gray-500">
                                                                {area2Text || "-"}
                                                            </span>
                                                            {activeArea === "area2" && (
                                                                <span className="ml-1 text-[10px] text-brand-600 font-medium">
                                                                    エリア2
                                                                </span>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAreaClick("area3")}
                                                            disabled={readOnly}
                                                            className={clsx(
                                                                "rounded px-1 -mx-1 transition-colors",
                                                                activeArea === "area3"
                                                                    ? "bg-brand-100 ring-2 ring-brand-500"
                                                                    : "hover:bg-gray-100",
                                                                readOnly && "cursor-default"
                                                            )}
                                                        >
                                                            <span className="text-xs text-gray-500">
                                                                {area3Text || "-"}
                                                            </span>
                                                            {activeArea === "area3" && (
                                                                <span className="ml-1 text-[10px] text-brand-600 font-medium">
                                                                    エリア3
                                                                </span>
                                                            )}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className={clsx(
                                                            "text-xs text-gray-500 px-1 -mx-1 rounded",
                                                            activeArea === "area2" && "bg-brand-50"
                                                        )}>
                                                            {area2Text || "-"}
                                                        </span>
                                                        <span className={clsx(
                                                            "text-xs text-gray-500 px-1 -mx-1 rounded",
                                                            activeArea === "area3" && "bg-brand-50"
                                                        )}>
                                                            {area3Text || "-"}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    {/* 時間セル（グリッド線 + シフトバー） */}
                                    <td
                                        className="relative p-0"
                                        style={{ width: gridWidth, height: 58 }}
                                    >
                                        {/* グリッド線 */}
                                        <div className="absolute inset-0 flex">
                                            {SAMPLE_HOURS.map((hour, idx) => (
                                                <div
                                                    key={hour}
                                                    className={clsx(
                                                        idx < SAMPLE_HOURS.length - 1 && "border-r border-gray-300"
                                                    )}
                                                    style={{ width: hourWidth }}
                                                />
                                            ))}
                                        </div>
                                        {/* シフトバー（緑バー表示） */}
                                        {shiftBarStyle && (
                                            <>
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        top: 10,
                                                        height: 8,
                                                        left: shiftBarStyle.left,
                                                        width: shiftBarStyle.width,
                                                        backgroundColor: "rgba(34, 197, 94, 0.35)",
                                                        borderRadius: 2,
                                                    }}
                                                />
                                                {/* 左端キャップ */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        top: 6,
                                                        height: 16,
                                                        left: shiftBarStyle.left,
                                                        width: 1.5,
                                                        backgroundColor: "rgba(34, 197, 94, 0.6)",
                                                    }}
                                                />
                                                {/* 右端キャップ */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        top: 6,
                                                        height: 16,
                                                        left: shiftBarStyle.left + shiftBarStyle.width - 1.5,
                                                        width: 1.5,
                                                        backgroundColor: "rgba(34, 197, 94, 0.6)",
                                                    }}
                                                />
                                            </>
                                        )}
                                        {/* 中央点線 */}
                                        <div
                                            className="absolute left-0 right-0 border-b border-dashed border-gray-200"
                                            style={{ top: 29 }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    // 選択肢パネル
    const renderOptionsPanel = () => {
        if (!activeArea || readOnly) return null;

        const currentValue = activeArea === "area2" ? value.area2 : value.area3;
        const handleChange = activeArea === "area2" ? handleArea2Change : handleArea3Change;

        return (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-3">
                    {activeArea === "area2" ? "エリア2" : "エリア3"}の表示内容を選択
                </div>
                <div className="flex flex-wrap gap-2">
                    {DAILY_AREA2_AND_3_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleChange(option.value)}
                            className={clsx(
                                "px-4 py-2 rounded-lg text-sm transition-colors",
                                currentValue === option.value
                                    ? "bg-brand-500 text-white"
                                    : "bg-white border border-gray-300 text-gray-700 hover:border-brand-300 hover:bg-brand-50"
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* 説明 */}
            <div className="text-sm text-gray-600 mb-4">
                ワースケ（日別シフト表）の従業員セルに表示する内容を設定します。
                下のプレビューで設定したいエリアをクリックしてください。
            </div>

            {/* グリッドプレビュー */}
            <div className="mb-4">
                <div className="text-xs text-gray-500 mb-2">プレビュー（クリックして編集）</div>
                {renderGridPreview()}
            </div>

            {/* 選択肢パネル */}
            {renderOptionsPanel()}

            {/* 現在の設定サマリー */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-2">現在の設定（クリックして変更）</div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="p-2 rounded-lg text-left">
                        <span className="text-gray-500">エリア1: </span>
                        <span className="font-medium text-gray-800">従業員名（固定）</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleAreaClick("area2")}
                        disabled={readOnly}
                        className={clsx(
                            "text-left p-2 rounded-lg transition-colors",
                            activeArea === "area2"
                                ? "bg-brand-100 ring-2 ring-brand-500"
                                : "hover:bg-white",
                            readOnly && "cursor-default"
                        )}
                    >
                        <span className="text-gray-500">エリア2: </span>
                        <span className="font-medium text-gray-800">
                            {DAILY_AREA2_AND_3_OPTIONS.find((o) => o.value === value.area2)?.label}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleAreaClick("area3")}
                        disabled={readOnly}
                        className={clsx(
                            "text-left p-2 rounded-lg transition-colors",
                            activeArea === "area3"
                                ? "bg-brand-100 ring-2 ring-brand-500"
                                : "hover:bg-white",
                            readOnly && "cursor-default"
                        )}
                    >
                        <span className="text-gray-500">エリア3: </span>
                        <span className="font-medium text-gray-800">
                            {DAILY_AREA2_AND_3_OPTIONS.find((o) => o.value === value.area3)?.label}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// アイコンコンポーネント
const ChevronRightIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
        />
    </svg>
);

export default DailyShiftCellSettings;
